import { useQuery } from "@tanstack/react-query";
import type { LengthUnit } from "@CommonUtils/cdtStudio/domain/studioTypes";
import { categoryLengthUnitOf } from "../domain/studioLengthUnit";
import { fetchCdtRelease } from "./cdtCompressionService";

export const CATEGORY_LENGTH_UNIT_QUERY_KEY = (merchflowId: number | undefined) =>
  ["cdt-studio-length-unit", merchflowId ?? 0] as const;

export interface CategoryLengthUnitQuery {
  /** The category's unit once its release has answered; undefined while it loads or after it
   *  failed, so no label claims a unit nobody confirmed. */
  unit: LengthUnit | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  retry: () => void;
}

/**
 * The merchflow's length unit, off its newest CDT release. A merchflow with no release (404)
 * or a release with no unit reads inches; a failed lookup is an error, never inches.
 */
export const useCategoryLengthUnit = (merchflowId: number | undefined): CategoryLengthUnitQuery => {
  const query = useQuery({
    queryKey: CATEGORY_LENGTH_UNIT_QUERY_KEY(merchflowId),
    enabled: merchflowId !== undefined,
    staleTime: 60_000,
    queryFn: async ({ signal }) => {
      if (merchflowId === undefined) throw new Error("merchflowId is required");
      return categoryLengthUnitOf(await fetchCdtRelease(merchflowId, undefined, signal));
    },
  });
  return {
    // No merchflow, no release to read: the documented default.
    unit: merchflowId === undefined ? categoryLengthUnitOf() : query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    retry: () => void query.refetch(),
  };
};
